import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress, Grid, IconButton, OutlinedInput, Typography } from "@mui/material";
import Modal from "components/ui/Modal";
import dayjs from "dayjs";
import SearchAndSelect from "features/order_client_details/components/SearchAndSelect";
import Selectinput from "features/order_client_details/components/Selectinput";
import CalenderComp from "features/Setting/order/components/CalenderComp";
import { useEffect, useState } from "react";
import { getIcon } from "utils/iconsConfig";
import Button from "../Button";
import { ConsistentIcon, styling } from "./CreateStockOrderModal";
import "./quickcustomerorder.css";
import apiClient from "lib/api-client";
import { toast } from "react-toastify";

const CERTIFICATION_OPTIONS = ["IGI", "DGLA", "SGL", "GIA", "GSI", "None"];
const DEFAULT_BOOK_TYPE = "CRM Quote";
const PRIORITY_OPTIONS = ["Standard", "Urgent"];

const getTenthValidDate = () => {
  const today = new Date();
  const cursor = new Date(today);
  cursor.setDate(today.getDate() + 1);
  let count = 0;

  while (count < 10) {
    if (cursor.getDay() !== 0) {
      count++;
    }
    if (count < 10) {
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return cursor;
};

function QuickCustomerOrder({ open, handleClose, actionType, clientOptions = [], designData = {}, customerQuoteData = {}, onGenerateSuccess }) {
  const [clientName, setClientName] = useState({
    name: "",
    partyNumber: null,
    partyCode: "",
  });
  const [certification, setCertification] = useState("");
  const [bookType, setBookType] = useState(DEFAULT_BOOK_TYPE);
  const [deliveryDate, setDeliveryDate] = useState(getTenthValidDate());
  const [showCalendar, setShowCalendar] = useState(false);
  const [priority, setPriority] = useState("Standard");
  const [purchaseOrderNo, setPurchaseOrderNo] = useState("");
  const [stampInstruction, setStampInstruction] = useState("");
  const [comment, setComment] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [emails, setEmails] = useState([]);
  const [canGenerate, setCanGenerate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const normalizedClientOptions = Array.from(
    new Map(
      clientOptions.map((item) => [
        `${String(item?.clientName || "")
          .trim()
          .toLowerCase()}-${String(item?.partyCode || "")
          .trim()
          .toUpperCase()}`,
        item,
      ])
    ).values()
  )
    .sort((a, b) => String(a?.clientName || "").localeCompare(String(b?.clientName || "")))
    .map((item) => ({
      label: `${item.clientName} (${item.partyCode})`,
      value: `${item.clientName} (${item.partyCode})`,
      ...item,
    }));
  const selectedClientLabel =
    clientName.name && clientName.partyCode
      ? (() => {
          const match = normalizedClientOptions.find((option) => option?.clientName === clientName?.name && option?.partyCode === clientName?.partyCode);
          return match ? `${match.clientName} (${match.partyCode})` : null;
        })()
      : null;

  useEffect(() => {
    if (!open) return;
    if (!deliveryDate) {
      setDeliveryDate(getTenthValidDate());
    }
  }, [open, deliveryDate]);

  useEffect(() => {
    if (clientName?.name && certification && bookType && deliveryDate && priority) {
      setCanGenerate(true);
    } else {
      setCanGenerate(false);
    }
  }, [clientName, certification, bookType, deliveryDate, priority]);

  const handleDateSelect = (date) => {
    setDeliveryDate(date);
    setShowCalendar(false);
  };

  const getPriorityColor = () => {
    if (priority === "Standard") return "#5078C8";
    if (priority === "Urgent") return "#DE9300";
    return "#595959";
  };

  const clearState = () => {
    setClientName({
      name: "",
      partyNumber: null,
      partyCode: "",
    });
    setCertification("");
    setBookType(DEFAULT_BOOK_TYPE);
    setDeliveryDate(getTenthValidDate());
    setPriority("Standard");
    setPurchaseOrderNo("");
    setStampInstruction("");
    setComment("");
    setPhoneNumbers([]);
    setEmails([]);
    handleClose();
  };
  const dismissModal = () => {
    setShowCalendar(false);
    handleClose();
  };

  const handleGenerateQuote = async () => {
    if (!canGenerate || isSubmitting) return;

    const normalizeGold = (value = "") => {
      const [goldQlty = "", goldColorName = ""] = value.split(" - ");
      const colorMap = { Pink: "P", Yellow: "Y", White: "W" };
      return {
        goldQlty: goldQlty?.trim() || null,
        goldColor: colorMap[goldColorName?.trim()] || null,
      };
    };

    const { goldQlty, goldColor } = normalizeGold(customerQuoteData?.goldQuality);
    const payload = {
      clientName: clientName?.name || "",
      partyNo: clientName?.partyNumber,
      partyCode: clientName?.partyCode || "",
      purchausOrder: purchaseOrderNo || "",
      bookType,
      certificate: certification || "",
      priority,
      deliveryDate: deliveryDate ? dayjs(deliveryDate).format("DD/MM/YYYY") : "",
      remarks: comment || "",
      stampInstruction: stampInstruction || "",
      designs: [
        {
          id: designData?.id || designData?._id || "",
          styleNumber: designData?.styleNumber || "",
          itemSize: customerQuoteData?.size && customerQuoteData?.size !== "N/A" ? customerQuoteData?.size : null,
          diaQlty: customerQuoteData?.diamondQuality || null,
          goldQlty,
          goldColor,
          message: customerQuoteData?.comment || null,
          rhodium: customerQuoteData?.rhodium ? customerQuoteData.rhodium[0]?.toUpperCase() : null,
          itemPoNo: purchaseOrderNo || "",
        },
      ],
      phoneNumbers,
      emails,
    };
    try {
      setIsSubmitting(true);
      await apiClient.post("/api/v3/client/vision_order/createVisionOrder", payload);
      toast.success("Quote generated successfully", { autoClose: 1200 });
      onGenerateSuccess?.();
      clearState();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to generate quote", {
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          }}
        >
          <CircularProgress color="success" />
        </div>
      )}
      <Modal
        open={open}
        handleClose={dismissModal}
        style={{
          width: "25vw",
          right: "28.5vw",
          top: "2vh",
          transform: "none",
          height: "auto", // IMPORTANT: fixed height
          maxHeight: "88vh",
          padding: "0px 6px 12px 18px", 
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "88vh", overflow: "hidden" }}>
        {/* HEADER */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "#fff",
            paddingTop: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "24px",
              lineHeight: "100%",
              letterSpacing: "0%",
            }}
          >
            Customer Quote Details
          </Typography>
          <IconButton onClick={clearState} size="small">
            <CloseIcon sx={{ fontSize: "1.2vw", color: "#000" }} />
          </IconButton>
        </Box>

        {/* SCROLLABLE CONTENT */}
        <Box
           sx={{
            flex: 1,
            overflowY: "auto",
            paddingRight: "4px",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-button": {
              display: "none",
            },
            "&::-webkit-scrollbar-track": {
              background: "#424242",
              borderRadius: "0px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#9e9e9e",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#bdbdbd",
            },
          }}
        >
          <Grid container spacing={"1.2vw"}>
            {/* Header */}
            <Grid
              item
              xs={12}
              className="qco-header"
              sx={{
                position: "sticky",
                top: "-20px",
                zIndex: 100,
                backgroundColor: "#fff",
              }}
            >
            </Grid>

            {/* Client Name */}
            <Grid item xs={12} className="sco-main-grid-item">
              <div className="sco-label-row">
                <span className="sco-label-icon">{getIcon("OrderClient")}</span>
                <Typography className="sco-select-header-create">Client Name</Typography>
              </div>
              <SearchAndSelect
                value={selectedClientLabel}
                onchange={(selectedOption) => {
                  if (!selectedOption || !selectedOption.partyCode) {
                    setClientName({
                      name: "",
                      partyNumber: null,
                      partyCode: "",
                    });
                    return;
                  }

                  setClientName({
                    name: selectedOption.clientName,
                    partyNumber: selectedOption.partyNo,
                    partyCode: selectedOption.partyCode,
                  });

                  const allEmails = [...(selectedOption.firmEmail || []), ...(selectedOption.otherFirmEmail || [])].filter((email) => email?.trim());
                  const allPhones = [...(selectedOption.whatsAppNo || []), ...(selectedOption.otherWhatsAppNo || [])]
                    .filter((phone) => {
                      const cleanPhone = phone?.replace(/^\+91|^91/, "").trim();
                      return cleanPhone && cleanPhone.length >= 10;
                    })
                    .map((phone) => phone.replace(/^\+91|^91/, ""));

                  setEmails(allEmails);
                  setPhoneNumbers(allPhones);
                }}
                options={normalizedClientOptions}
                handleSearchClear={() => {
                  setClientName({
                    name: "",
                    partyNumber: null,
                    partyCode: "",
                  });
                  setPhoneNumbers([]);
                  setEmails([]);
                }}
                useBlackTheme={true}
                notFoundOption={{
                  label: "No client found",
                }}
              />
            </Grid>

            {/* Certification */}
            <Grid item xs={12} className="sco-main-grid-item">
              <div className="sco-label-row">
                <span className="sco-label-icon">{getIcon("Certificationicon")}</span>
                <Typography className="sco-select-header-create">Certification</Typography>
              </div>
              <Selectinput
                value={certification}
                onchange={(e) => setCertification(e.target.value)}
                options={CERTIFICATION_OPTIONS}
                useMuiArrow={true}
                placeHolder="Select"
                centerOptions={true}
                customStyle={{
                  ...styling,
                  "& .MuiSelect-select": {
                    color: certification ? "#000" : "#9e9e9e",
                    WebkitTextFillColor: certification ? "#000" : "#9e9e9e",
                  },
                }}
              />
            </Grid>

            {/* Book Type */}
            <Grid item xs={12} className="sco-main-grid-item">
              <div className="sco-label-row">
                <span className="sco-label-icon">{getIcon("BookTypeIcon")}</span>
                <Typography className="sco-select-header-create">Book Type</Typography>
              </div>
              <OutlinedInput value={bookType} disabled className="sco-date-input" />
            </Grid>

            {/* Delivery Date */}
            <Grid item xs={12} className="sco-main-grid-item">
              <div className="sco-label-row">
                <span className="sco-label-icon">
                  <ConsistentIcon type="date" color="black" />
                </span>
                <Typography className="sco-select-header-create">Delivery Date</Typography>
              </div>
              <OutlinedInput
                value={deliveryDate ? dayjs(deliveryDate).format("Do MMM YYYY") : ""}
                onClick={() => setShowCalendar(true)}
                readOnly
                className="sco-date-input"
                placeholder="Select"
                sx={{
                  color: deliveryDate ? "#000" : "#9e9e9e",
                  "& input::placeholder": {
                    color: "#9e9e9e",
                    opacity: 1,
                  },
                }}
              />
            </Grid>

            {/* Priority */}
            <Grid item xs={12} className="sco-main-grid-item">
              <div className="sco-label-row">
                <span className="sco-label-icon">{getIcon("PriorityOrderIcon")}</span>
                <Typography className="sco-select-header-create">Priority</Typography>
              </div>
              <Selectinput
                value={priority}
                onchange={(e) => setPriority(e.target.value)}
                options={PRIORITY_OPTIONS}
                placeHolder="Select priority"
                customStyle={{
                  ...styling,
                  "& .MuiSelect-select": {
                    color: getPriorityColor(),
                    WebkitTextFillColor: getPriorityColor(),
                  },
                }}
              />
            </Grid>

            {/* Purchase Order No (Optional) */}

            <Grid item xs={12} className="sco-main-grid-item">
              <div className="sco-label-row">
                <span className="sco-label-icon">{getIcon("PruchaseOrderIcon")}</span>
                <Typography className="sco-select-header-create">
                  Purchase Order No<span style={{ color: "#8B8B8B" }}>(Optional)</span>
                </Typography>
              </div>
              <Grid item xs={12} className="qco-main-grid-item">
                <textarea
                  placeholder="Enter purchase order number"
                  value={purchaseOrderNo}
                  onChange={(e) => setPurchaseOrderNo(e.target.value)}
                  className="sco-comment-box"
                  style={{ height: "6px", background: "#fff", border: "1px solid black", borderRadius: "5px" }}
                />
              </Grid>
            </Grid>

            {/* Stamp Instruction (Optional) */}
            <Grid item xs={12} className="sco-main-grid-item">
              <div className="sco-label-row">
                <span className="sco-label-icon">{getIcon("StampInstructionicon")}</span>
                <Typography className="sco-select-header-create">
                  Stamp Instruction<span style={{ color: "#8B8B8B" }}>(Optional)</span>
                </Typography>
              </div>
              <Grid item xs={12} className="sco-main-grid-item">
                <textarea
                  placeholder="Enter instructions"
                  value={stampInstruction}
                  onChange={(e) => setStampInstruction(e.target.value)}
                  className="sco-comment-box"
                  style={{ height: "6px", background: "#fff", border: "1px solid black", borderRadius: "5px" }}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} className="sco-main-grid-item">
              <textarea placeholder="Enter your message" value={comment} onChange={(e) => setComment(e.target.value)} className="sco-comment-box" />
            </Grid>
          </Grid>
        </Box>

        {/* FOOTER BUTTON */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            paddingTop: "10px",
            paddingBottom: "10px",
            paddingRight: "10px",
            zIndex: 10,
          }}
        >
          <Button
            onClick={handleGenerateQuote}
            sx={{
              width:"100%",
              height: "6.5vh",
              backgroundColor: canGenerate ? "#50C878" : "#444444",
              color: "#000",
              textTransform: "none",
              fontFamily: "Poppins",
              fontSize: "18px",
              "&:hover": {
                backgroundColor: canGenerate ? "#50C878" : "#444444",
              },
            }}
          >
            {isSubmitting ? "Generating..." : "Generate Quote"}
          </Button>
        </Box>
        </Box>

        <CalenderComp
          open={showCalendar}
          handleClose={() => setShowCalendar(false)}
          onDateSelect={handleDateSelect}
          disableRestrictedDates={true}
          autoSelect={true}
          compact={true}
          isCreateProject={false}
        />
      </Modal>
    </>
  );
}

export default QuickCustomerOrder;
